// Webcam hand-tracking "air-slicing" mode. Constants are playtested (CLAUDE.md §4):
// 1280×720 feed, MediaPipe tasks-vision 0.10.14 HandLandmarker on GPU, central-region
// mapping, One-Euro filtering, velocity extrapolation, adaptive 60fps blade glide, and a
// hysteretic "index-extended" blade gate. Detection only sets TARGETS; the blade is
// animated every render frame in camTick() — that's what makes the trail smooth, not dotted.
//
// Detection runs in a Web Worker (camera-worker.ts) so the heavy synchronous detectForVideo()
// call can't block game rendering (the biggest camera-latency win). If the worker can't
// initialise, we fall back to running detection on the main thread so camera mode still works.
import { G } from '../game/state';
import { reset } from '../game/state';
import { bladeSegment } from '../game/slice';
import { landmarkToScreen } from './videoMap';
import type { EuroFilter } from '../game/types';

// MediaPipe assets (also used inside the worker).
const MP_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/vision_bundle.mjs';
const MP_WASM = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const MP_MODEL = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

type LM = { x: number; y: number; z?: number };

let worker: Worker | null = null;
let inFlight = false; // one frame in the worker at a time → always send the freshest frame
let inFlightAt = 0;
let lastPumpVT = -1;

export async function startCamera(): Promise<void> {
  G.camMode = true;
  G.state = 'camwait';
  G.camErr = null;
  G.raiseFrames = 0;
  // Already initialised (e.g. returned to the menu via "One more shift", then chose camera
  // again) — just re-enter camwait; don't spin up a second worker/stream.
  if (G.camReady && (worker || G.landmarker) && G.video.srcObject) return;
  // GPU delegate needs WebGL; log availability so a CPU fallback is diagnosable (step b).
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    console.log('[camera] WebGL2 available:', !!gl, '· requesting MediaPipe delegate: GPU');
  } catch {
    /* ignore */
  }
  try {
    // 16:9 wide feed — requesting 4:3 makes most webcams CROP the sides, shrinking the play area
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
    });
    G.video.srcObject = stream;
    await G.video.play();
    if (canUseWorker()) startWorkerDetection();
    else await startMainThreadDetection();
  } catch (err: any) {
    G.camErr = err?.message || 'camera unavailable';
  }
}

function canUseWorker(): boolean {
  return typeof Worker !== 'undefined' && typeof createImageBitmap === 'function';
}

// ---------- worker detection path (preferred) ----------
function startWorkerDetection(): void {
  try {
    // Classic (non-module) worker on purpose: MediaPipe's wasm loader calls importScripts(),
    // which is forbidden in module workers. The worker uses a runtime dynamic import() for the
    // MediaPipe API, which classic workers support.
    worker = new Worker(new URL('./camera-worker.ts', import.meta.url));
  } catch (e) {
    console.warn('[camera] worker unavailable, using main thread', e);
    void startMainThreadDetection();
    return;
  }
  worker.onmessage = (e: MessageEvent) => {
    const m = e.data;
    if (m.type === 'ready') {
      G.camReady = true;
      G.camPath = 'worker';
      console.log('[camera] detection running in Web Worker');
    } else if (m.type === 'error') {
      console.warn('[camera] worker init failed, falling back to main thread:', m.msg);
      worker?.terminate();
      worker = null;
      void startMainThreadDetection();
    } else if (m.type === 'result') {
      inFlight = false;
      G.detMs = m.detMs;
      handleHands((m.landmarks as LM[][]) || []);
    }
  };
  worker.postMessage({ type: 'init', wasmBase: MP_WASM, modelUrl: MP_MODEL });
}

// Called every render frame (from camTick): grab the freshest video frame and hand it to the
// worker. Gated so only one frame is in flight at a time — keeps latency low and avoids backlog.
function pumpFrame(now: number): void {
  if (!worker || !G.camReady || inFlight) {
    // recover if a result was lost (e.g. worker hiccup) so the pump never stalls
    if (inFlight && now - inFlightAt > 500) inFlight = false;
    return;
  }
  if (G.video.readyState < 2 || G.video.currentTime === lastPumpVT) return; // no new frame
  lastPumpVT = G.video.currentTime;
  inFlight = true;
  inFlightAt = now;
  createImageBitmap(G.video)
    .then((bmp) => {
      if (!worker || !G.camMode) {
        bmp.close?.();
        inFlight = false;
        return;
      }
      worker.postMessage({ type: 'frame', bitmap: bmp, ts: now }, [bmp]);
    })
    .catch(() => {
      inFlight = false;
    });
}

// ---------- main-thread detection path (fallback) ----------
async function startMainThreadDetection(): Promise<void> {
  if (G.landmarker) {
    G.camReady = true;
    return;
  }
  try {
    const mp: any = await import(/* @vite-ignore */ MP_URL);
    const files = await mp.FilesetResolver.forVisionTasks(MP_WASM);
    G.landmarker = await mp.HandLandmarker.createFromOptions(files, {
      baseOptions: { modelAssetPath: MP_MODEL, delegate: 'GPU' },
      runningMode: 'VIDEO',
      numHands: 2,
      minHandDetectionConfidence: 0.4,
      minHandPresenceConfidence: 0.4,
      minTrackingConfidence: 0.4,
    });
    G.camReady = true;
    G.camPath = 'main';
    console.log('[camera] detection running on the main thread (fallback)');
    requestAnimationFrame(mainThreadLoop);
  } catch (err: any) {
    G.camErr = err?.message || 'camera unavailable';
  }
}

function mainThreadLoop(): void {
  if (!G.camMode || G.camPath !== 'main') return;
  if (G.landmarker && G.video.readyState >= 2 && G.video.currentTime !== G.lastVT) {
    G.lastVT = G.video.currentTime;
    let res: any = null;
    const t0 = performance.now();
    try {
      res = G.landmarker.detectForVideo(G.video, performance.now());
    } catch (e) {
      /* transient detect error — skip this frame */
    }
    G.detMs = performance.now() - t0;
    if (res) handleHands(res.landmarks || []);
  }
  requestAnimationFrame(mainThreadLoop);
}

// One Euro filter: strong smoothing at rest (kills jitter), almost none during fast swipes.
function makeEuro(minCutoff: number, beta: number, dCutoff: number): EuroFilter {
  let x: number | null = null;
  let dx = 0;
  let tPrev = 0;
  const alpha = (c: number, dt: number): number => {
    const r = 2 * Math.PI * c * dt;
    return r / (r + 1);
  };
  return (v: number, ts: number): number => {
    if (x === null) {
      x = v;
      tPrev = ts;
      return v;
    }
    const dt = Math.max(0.001, (ts - tPrev) / 1000);
    tPrev = ts;
    const dxRaw = (v - x) / dt;
    dx = dx + alpha(dCutoff, dt) * (dxRaw - dx);
    const c = minCutoff + beta * Math.abs(dx);
    x = x + alpha(c, dt) * (v - x);
    return x;
  };
}

function mapTip(lm: LM[]): { x: number; y: number } {
  const tip = lm[8]; // index fingertip
  // Map through the SAME mirrored cover-fit transform the video is drawn with → the cursor sits
  // on the fingertip pixel everywhere on screen (no amplified central-region mapping).
  const vw = G.video.videoWidth || G.W;
  const vh = G.video.videoHeight || G.H;
  return landmarkToScreen(tip.x, tip.y, G.W, G.H, vw, vh);
}

// >1: index fingertip(8) further from wrist(0) than its knuckle(6) → finger extended.
function indexRatio(lm: LM[]): number {
  const d = (a: number, b: number): number => Math.hypot(lm[a].x - lm[b].x, lm[a].y - lm[b].y);
  const base = d(6, 0);
  return base > 0 ? d(8, 0) / base : 0;
}

// Consume a batch of landmark sets (from either detection path) and update per-hand targets.
function handleHands(lms: LM[][]): void {
  const now = performance.now();
  G.detCount++;
  if (now - G.detTick > 1000) {
    G.detFps = G.detCount;
    G.detCount = 0;
    G.detTick = now;
  }
  const dets = lms.slice(0, 2).map((lm) => ({ pt: mapTip(lm), r: indexRatio(lm) }));
  // keep hand identity stable across frames (nearest-match), else blades jump between hands
  const used = [false, false];
  const assign: (typeof dets[number] | null)[] = [null, null];
  for (const d of dets) {
    let best = -1;
    let bd = Infinity;
    for (let i = 0; i < 2; i++) {
      if (used[i]) continue;
      const pr = G.camHands[i].p || G.camHands[i].target;
      const dist = pr ? Math.hypot(d.pt.x - pr.x, d.pt.y - pr.y) : 1e6 + i;
      if (dist < bd) {
        bd = dist;
        best = i;
      }
    }
    if (best >= 0) {
      used[best] = true;
      assign[best] = d;
    }
  }
  for (let i = 0; i < 2; i++) {
    const h = G.camHands[i];
    const d = assign[i];
    if (!d) {
      if (h.target) {
        h.lostAt = now;
        h.target = null;
        h.active = false;
        h.fx = h.fy = null;
      }
      continue;
    }
    if (!h.fx) {
      // Dialed toward RESPONSIVENESS now the cursor is glued to the finger: high minCutoff (little
      // smoothing at rest) + high beta (little smoothing on fast moves). Prefer slight jitter over lag.
      h.fx = makeEuro(1.8, 0.06, 1.0);
      h.fy = makeEuro(1.8, 0.06, 1.0);
    }
    const px = h.fx(d.pt.x, now);
    const py = h.fy!(d.pt.y, now); // One-Euro-filtered target
    if (h.target && h.detAt) {
      const dtd = Math.max(0.016, (now - h.detAt) / 1000);
      const nvx = (px - h.target.x) / dtd;
      const nvy = (py - h.target.y) / dtd;
      h.tv = { x: h.tv.x * 0.4 + nvx * 0.6, y: h.tv.y * 0.4 + nvy * 0.6 }; // smoothed velocity
    } else h.tv = { x: 0, y: 0 };
    // blade gate: index finger out = armed, fist = sheathed (hysteresis so it doesn't flicker)
    h.active = d.r > (h.active ? 1.05 : 1.2);
    h.target = { x: px, y: py };
    h.detAt = now;
  }
  if (G.state === 'camwait') {
    if (dets.some((d) => d.r > 1.2)) {
      G.raiseFrames++;
      if (G.raiseFrames > 20) reset();
    } else G.raiseFrames = 0;
  }
}

// Runs every render frame: pump a frame to the worker, then glide blades toward targets so the
// trail stays dense and smooth regardless of the (slower) detection rate.
export function camTick(dt: number): void {
  if (!G.camMode) return;
  const now = performance.now();
  if (worker) pumpFrame(now);
  for (const h of G.camHands) {
    if (h.target) {
      // Only a small (~1 frame) extrapolation to cover detection latency — just enough to kill
      // lag without the cursor overshooting past the fingertip.
      const ex = Math.min((now - h.detAt) / 1000, 0.02); // max ~20ms (~1 frame)
      let exx = h.tv.x * ex;
      let exy = h.tv.y * ex;
      const exl = Math.hypot(exx, exy);
      if (exl > 40) {
        exx *= 40 / exl;
        exy *= 40 / exl;
      }
      const tx = h.target.x + exx;
      const ty = h.target.y + exy;
      if (!h.p) {
        h.p = { x: tx, y: ty };
        continue;
      }
      const dist = Math.hypot(tx - h.p.x, ty - h.p.y);
      // FAST glide — now purely to smooth the ~2 render frames between detections, not for style.
      // High base rate so the cursor reaches the detected target within ~1-2 frames (glued, not laggy).
      const k = 1 - Math.exp(-dt * Math.min(150, 80 + dist * 0.6));
      const nx = h.p.x + (tx - h.p.x) * k;
      const ny = h.p.y + (ty - h.p.y) * k;
      if (h.active) {
        // cursor always follows; the BLADE only cuts with index finger out.
        // Sub-sample the glide segment so the ribbon stays dense and smooth even if the frame
        // rate dips (few points/frame is what makes the trail look angular + laggy).
        const px = h.p.x;
        const py = h.p.y;
        const steps = Math.max(1, Math.min(6, Math.round(Math.hypot(nx - px, ny - py) / 14)));
        for (let s = 1; s <= steps; s++) {
          G.trail.push({ x: px + ((nx - px) * s) / steps, y: py + ((ny - py) * s) / steps, t: now });
        }
        if (G.state !== 'camwait' && G.state !== 'dead') bladeSegment(px, py, nx, ny);
      }
      h.p = { x: nx, y: ny };
    } else if (h.p && now - h.lostAt > 250) {
      h.p = null;
    }
  }
}
